import { yupResolver } from '@hookform/resolvers/yup';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    AccordionSummary,
    AccordionDetails,
    Accordion,
    Box,
    Button,
    Checkbox,
    Typography,
    IconButton
} from "@mui/material";
import { apiGet } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import BenefitSettingsModal from "components/BenefitSettingsModal";
import TableAllData from "components/Content/CustomTable/TableAllData";
import TableStyle from "components/Content/Table/TableStyle.module.css";
import moment from "moment";
import LoaderAnimation from "pages/LoaderAnimation";
import React, { useEffect, useState, useContext } from "react";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import PdfViewerModal from './PdfViewerModal';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import AximTooltip from './AximTooltip';
import { Utils } from 'common/Utils';
import ConfirmDialog from './Content/Modal/ConfirmDialog';

export default function PlanSettings ({ refresh, isAdmin, isReadonly }) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const validationSchema = Yup.object().shape({
    });
    const {
        register,
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema)
    });
    
    const [checkingData, setCheckingData] = useState(true)
    const [checkingDetailData, setCheckingDetailData] = useState(false);
    const [openModal, setOpen] = useState(false)
    const [currentBenefit, setCurrentBenefit] = useState({})

    const [planColumns, setPlanColumns] = useState([]);
    const [planData, setPlanData] = useState([])
    const [expanded, setExpanded] = React.useState(-1)

    const [isShowingPlans, setIsShowingPlans] = useState(true);

    const [documents, setDocuments] = useState([]);
    const [openPdfViewer, setOpenPdfViewer] = useState(false);
    const [document, setDocument] = useState(null);
    const [selectedDocuments, setSelectedDocuments] = useState([]);

    const [doShowConfirm, setDoShowConfirm] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        if (refresh > 0) {
            fetchPlans();
        }

        var planActionColumn = 
        {
            Header: "Action",
            accessor: (cell) => (
                <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="span"
                    onClick={() => handleEdit(cell)}
                >
                    <EditOutlinedIcon />
                </IconButton>),
            disableFilters: true,
        };

        if (isReadonly || !isAdmin) {
            setPlanColumns(defaultPlanColumns);
        }
        else {
            setPlanColumns([planActionColumn, ...defaultPlanColumns]);
        }

    }, [refresh, companyId, isReadonly]);

    useEffect(() => {
        if (expanded !== -1 && planData && planData.length > expanded) {
            if (isShowingPlans) {
                fetchPlanDetail(planData[expanded]);
            } else {
                fetchDocuments(planData[expanded]);
            }
        }
    },[expanded, isShowingPlans, refresh])

    const fetchPlans = () => {
        setCheckingData(true);

        apiGet(apiUrls.getPlans(companyId))
            .then((response) => {
                setPlanData(response.data);
            })
            .catch(function (e) {
                // console.log("THIS IS ERROR", error);  
                setPlanData([]);
            })
            .then(function () {
                // always executed
                setCheckingData(false);
            });
    };

    const fetchPlanDetail = (planObj) => {
        if (planObj && planObj.planPeriodID) {
            setCheckingDetailData(true);

            apiGet(apiUrls.getPlanBenefits(companyId, planObj?.planPeriodID))
                .then((response) => {
                    let planDataCopy = [...planData]
                    let updatePlan = planDataCopy.find(p => p.planPeriodID == planObj.planPeriodID)
                    if (updatePlan != null) {
                        updatePlan.benefits = response.data
                        setPlanData(planDataCopy)
                    }
                })
                .catch(function (e) {
                    // console.log("THIS IS ERROR", error);
                    setPlanData([]);
                })
                .then(function () {
                    // always executed
                    setCheckingDetailData(false);
                });
        }
    }
    
    const handleEdit = (benefit) => {
        // console.dir(benefit)
        // console.dir('setCurrentBenefit')
        setCurrentBenefit({ ...benefit })
        setOpen(true)
    }

    let defaultPlanColumns = [
        {
            Header: "Name",
            accessor: "benefitName"
        },
        {
            Header: "Carrier",
            accessor: "carrierName"
        },
        {
            Header: "Benefit Type",
            accessor: "benefitTypeName"
        },
        {
            Header: "Calculation Type",
            accessor: (cell) => (cell.benefitConfiguration.variableTypeName)
        },
        {
            Header: "Fringe Cost",
            accessor: (cell) => (cell.benefitConfiguration.isFringeCost?"Yes":"No")
        }
    ];

    const handleChange = (expandedKey) => {
        if (expandedKey != expanded) {
            setExpanded(expandedKey)
        }
        else {
            setExpanded(-1)
        }
    };

    const toggleView = (e) => {
        setIsShowingPlans((prev) => !prev);
        e.stopPropagation();
    }

    const documentsColumns = [
        {
            id: "Action",
            Header: "",
            accessor: (cell) => 
                <>
                    <Checkbox color="primary" onChange={() => toggleSelectDocument(cell)} checked={isDocumentSelected(cell)} />
                    <AximTooltip arrow title="Download Document">
                        <IconButton color="primary" aria-label="download document" component="span"
                            onClick={() => promptDownloadDocument(cell)} >
                            <CloudDownloadOutlinedIcon />
                        </IconButton>
                    </AximTooltip>
                    {cell.filename.endsWith(".pdf") ? 
                        <AximTooltip arrow title="View Document">
                            <IconButton color="primary" aria-label="view document" component="span"
                                onClick={() => showDocument(cell)} >
                                <VisibilityOutlinedIcon />
                            </IconButton>
                        </AximTooltip>
                        : null
                    }
                </>,
            disableFilters: true,
        },
        {
            Header: "File Name",
            accessor: "filename"
        },
        {
            Header: "Description",
            accessor: "description"
        }
    ];

    const fetchDocuments = (planObj) => {
        setCheckingDetailData(true);

        apiGet(apiUrls.getPlanDocuments(companyId, planObj.planPeriodID))
            .then((response) => {
                setDocuments(response.data);
            })
            .catch((e) => {
                setDocuments([]);
            })
            .finally(() => {
                setCheckingDetailData(false);
                setSelectedDocuments([]);
            });
    }

    const promptDownloadAll = (planObj) => {
        setConfirmTitle("Download Documents");
        setConfirmText(`Are you sure you would like to download all documents?`);
        setDoShowConfirm(true);
        setConfirmAction(() => (result) => downloadAll(result, planObj));
    }

    const downloadAll = (result, planObj) => {
        if (result) {
            Utils.downloadFile(apiUrls.downloadAllPlanDocuments(companyId, planObj.planPeriodID),
            null,
            null,
            () => {
                error.setTitle("An Error Occurred");
                error.setText("Unable to download documents.");
                error.setDoShowError(true);
            },
            () => {
            //   setConfirmButtonsDiabled(false);
            //   setDoShowConfirm(false);
            });
        }
        
        setDoShowConfirm(false);
    }

    const promptDownloadSelected = (planObj) => {
        setConfirmTitle("Download Documents");
        setConfirmText(`Are you sure you would like to download the selected documents?`);
        setDoShowConfirm(true);
        setConfirmAction(() => (result) => downloadSelected(result, planObj));
    }

    const downloadSelected = (result, planObj) => {
        if (result) {
            Utils.downloadFile(apiUrls.downloadSelectedPlanDocuments(companyId, planObj.planPeriodID, selectedDocuments),
            null,
            null,
            () => {
                error.setTitle("An Error Occurred");
                error.setText("Unable to download documents.");
                error.setDoShowError(true);
            },
            () => {
                setSelectedDocuments([]);
            },
            true,
            selectedDocuments);
        }
        
        setDoShowConfirm(false);
    }

    const toggleSelectDocument = (cell) => {
        if (isDocumentSelected(cell)) {
            let index = selectedDocuments.indexOf(cell.documentId);

            setSelectedDocuments(selectedDocuments.filter((_, i) => i != index));
        }
        else {
            setSelectedDocuments([...selectedDocuments, cell.documentId]);
        }
    }

    const isDocumentSelected = (cell) => {
        return selectedDocuments.some(d => d === cell.documentId);
    }

    const showDocument = (cell) => {
        apiGet(apiUrls.getPlanDocument(companyId, cell.documentId))
            .then((response) => {
                setDocument(response.data);
            })
            .catch((e) => {
                setDocument(null);

                error.setTitle("An Error Occurred");
                error.setText("Unable to display document.");
                error.setDoShowError(true);
            })
            .finally(() => {
                setOpenPdfViewer(true);
            });
    }

    const promptDownloadDocument = (cell) => {
        setConfirmTitle("Download Document");
        setConfirmText(`Are you sure you would like to download this document?`);
        setDoShowConfirm(true);
        setConfirmAction(() => (result) => downloadDocument(result, cell));
    }

    const downloadDocument = (result, cell) => {
        if (result) {
            Utils.downloadFile(apiUrls.downloadPlanDocument(companyId, cell.documentId),
            cell.filename,
            null,
            () => {
                error.setTitle("An Error Occurred");
                error.setText("Unable to download document.");
                error.setDoShowError(true);
            },
            () => {
            //   setConfirmButtonsDiabled(false);
            //   setDoShowConfirm(false);
            });
        }

        setDoShowConfirm(false);
    }

    const handleConfirmClose = (result) => {
        confirmAction(result);
    }

    return (
        <>
            <BenefitSettingsModal key="benefitModal" open={openModal}
                benefit={currentBenefit}
                callbackCloseModal={() => {
                    setOpen(false)
                    //refresh plan
                    let benefitPlan = planData.find(p=>p.planPeriodID==currentBenefit.planPeriodID)
                    fetchPlanDetail(benefitPlan)
                }}
            />
            {document ? 
                <PdfViewerModal
                    open={openPdfViewer}
                    documentId={document.documentId}
                    filename={document.filename}
                    description={document.description}
                    contents={document.contents}
                    handleClose={() => {
                        setOpenPdfViewer(false);
                        setDocument(null);
                    }}
                    handleDownloadUrl={apiUrls.downloadPlanDocument(companyId, document.documentId)}
                />
            : null}

            <ConfirmDialog
                doShowConfirm={doShowConfirm}
                handleClose={handleConfirmClose}
                confirmTitle={confirmTitle}
                confirmText={confirmText} />

            {checkingData ?
                <LoaderAnimation /> :
                planData.map((plan, key) => {
                    return (
                        <Accordion
                            key={key}
                            expanded={expanded === key}
                            onChange={() => { handleChange(key) }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="h6">
                                        {moment(plan.beginDate).format("MM/DD/YYYY")} to {moment(plan.endDate).format("MM/DD/YYYY")} - {plan.planPeriodName}
                                    </Typography>
                                    {expanded === key ? 
                                        <Button sx={{ right: 0 }} variant="outlined" onClick={toggleView}>
                                            {isShowingPlans ? "View Documents" : "View Benefits"}
                                        </Button>
                                    : null}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {checkingDetailData ? <LoaderAnimation />
                                : isShowingPlans ?
                                    <TableAllData
                                        className={TableStyle.TimeSheetsWrapper}
                                        columns={planColumns}
                                        data={plan.benefits} 
                                    />
                                :
                                    <TableAllData
                                        headerContents={
                                            <>
                                                <Button
                                                    startIcon={<CloudDownloadOutlinedIcon />}
                                                    variant="outlined"
                                                    onClick={() => promptDownloadAll(plan)}
                                                    disabled={documents?.length == 0}
                                                >
                                                    Download All
                                                </Button>
                                                {selectedDocuments.length > 0 ?
                                                    <Button
                                                        startIcon={<CloudDownloadOutlinedIcon />}
                                                        variant="outlined"
                                                        onClick={() => promptDownloadSelected(plan)}
                                                        className="ms-2"
                                                    >
                                                        {`Download Selected (${selectedDocuments.length})`}
                                                    </Button>
                                                : null}
                                            </>}
                                        columns={documentsColumns}
                                        data={documents}
                                    />
                                }
                            </AccordionDetails>
                        </Accordion>
                    );
                })
            }
        </>
    )
}